import { coletarEstadosFormulas } from '@shared/components/formula/utils/formula-estados.util';
import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { FormulaEditorDialogComponent, FormulaEditorDialogData, FormulaEditorDialogResult } from '@shared/components/formula/formula-editor-dialog/formula-editor-dialog.component';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaCatalogoService } from '@shared/services/apis/motor-fiscal/formula-catalogo.service';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { RegimeTributarioEnum } from '@shared/enums/motor-fiscal/regime-tributario.enum';
import { ConfiguracoesAvancadasSectionComponent } from './sections/configuracoes-avancadas-section/configuracoes-avancadas-section.component';
import { RetencoesFonteSectionComponent } from './sections/retencoes-fonte-section/retencoes-fonte-section.component';
import { IssqnCompetenciaSectionComponent } from './sections/issqn-competencia-section/issqn-competencia-section.component';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';

/**
 * Aba Totais (Totais) — base.
 * Configurações avançadas (4 indicadores) e Data de competência do ISSQN são
 * persistidas. A personalização de fórmulas usa o editor padrão (escopo "Totais").
 *
 * Os campos de Retenções na fonte (base de cálculo e alíquotas) são exibidos como
 * base visual desta etapa; ainda não há colunas no banco para persistí-los, então
 * apenas o flag "retencaoFonteAtivo" é salvo.
 */
@Component({
    selector: 'app-natureza-operacao-totais-tab',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AlertCardComponent,
        ConfiguracoesAvancadasSectionComponent,
        RetencoesFonteSectionComponent,
        IssqnCompetenciaSectionComponent,
    ],
    templateUrl: './natureza-operacao-totais-tab.component.html',
    styleUrl: './natureza-operacao-totais-tab.component.css',
})
export class NaturezaOperacaoTotaisTabComponent {
    @Input({ required: true }) form!: FormGroup;
    @Input() formulasTotais: FormulaCatalogoItemStruct[] = [];
    @Input() estadosFormulas: FormulaItemStruct[] = [];
    @Input() tiposBaseCalculoRetencao: LookupItemStruct[] = [];
    @Input() indicadoresComposicaoTotalItem: LookupItemStruct[] = [];
    @Input() indicadoresDeducaoDesoneracaoIcms: LookupItemStruct[] = [];
    @Input() indicadoresComposicaoTotalPisSt: LookupItemStruct[] = [];
    @Input() indicadoresComposicaoTotalCofinsSt: LookupItemStruct[] = [];
    @Input() tiposOperacaoVeiculo: LookupItemStruct[] = [];
    @Input() regimesEspeciaisTributacaoIssqn: LookupItemStruct[] = [];

    constructor(
        private dialog: MatDialog,
        private formulaCatalogoService: FormulaCatalogoService,
    ) {}

    public get totaisForm(): FormGroup {
        return this.form.get('totais') as FormGroup;
    }

    /** Fonte única em memória das fórmulas: o FormGroup, que o salvar() também lê.
        O @Input estadosFormulas serve só de seed inicial (o pai já popula o control). */
    private get estadosFormulasMemoria(): FormulaItemStruct[] {
        return (this.totaisForm.get('_estadosFormulas')?.value as FormulaItemStruct[]) ?? [];
    }

    public get personalizadasCount(): number {
        return this.estadosFormulasMemoria.filter(e => e.tipo === TipoFormulaEnum.Personalizada).length;
    }

    public get pisAtivoValue(): boolean {
        return !!this.form.get('pis.isAtivo')?.value;
    }

    public get cofinsAtivoValue(): boolean {
        return !!this.form.get('cofins.isAtivo')?.value;
    }

    /** Aba de ICMS visível conforme o regime: Simples Nacional usa "icmsSn", os demais usam "icms". */
    public get icmsOuSnAtivoValue(): boolean {
        const regime = this.form.get('regimeTributario')?.value;
        const grupo = regime === RegimeTributarioEnum.SimplesNacional ? 'icmsSn' : 'icms';
        return !!this.form.get(grupo)?.get('isAtivo')?.value;
    }

    public abrirEditorFormulas(): void {
        const data: FormulaEditorDialogData = {
            titulo: 'Totais',
            formulas: this.formulasTotais,
            estadosIniciais: this.estadosFormulasMemoria,
            estadosFormulasAgregados: coletarEstadosFormulas(this.form),
            contextoRaiz: 'natureza-operacao',
            validarPersonalizacoes: request => this.formulaCatalogoService.validarPersonalizacoes(request),
        };

        const ref = this.dialog.open(FormulaEditorDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });

        ref.afterClosed().subscribe((result: FormulaEditorDialogResult | null) => {
            if (result) {
                this.totaisForm.get('_estadosFormulas')?.setValue(result.formulas);
            }
        });
    }
}
